/**
 * La recherche d'un sous-traitant, vue par l'écran — le calcul, hors de React.
 *
 * Même découpe que `ot.ts` et `emballage.ts`, pour la même raison : ce qui est
 * ici se teste sans navigateur, sans base et sans session.
 *
 * DEUX CHOSES QUE CE FICHIER SAIT :
 *
 *   - **traduire un code postal en zone.** C'est le premier pas du processus
 *     réel — « ville → code zone » — et l'écran doit pouvoir le faire pour
 *     déduire l'axe d'un ordre sans rien demander à personne ;
 *   - **dire POURQUOI on n'a trouvé personne.** « Un écran vide qui n'explique
 *     pas est un écran raté » : la phrase doit nommer le filtre qui a tout
 *     écarté, sinon l'exploitant ne sait pas lequel relâcher.
 */

import type { ExplicationVide, Pays, Zone } from './api.js';

/**
 * Le code de zone d'une adresse : « 45770 » en France donne « FR45 ».
 *
 * LE PAYS VIENT DE L'ÉTAPE, il ne se devine pas du code postal — « 28906 »
 * s'écrit pareil à Getafe et dans le Finistère. C'est tout l'objet de
 * `etapes.site_pays`, posé le 06/09/2026.
 *
 * `null` quand l'un des deux manque : une étape saisie avant cette date n'a
 * pas de pays, et inventer « France » enverrait un camion au mauvais endroit.
 *
 * Les deux premiers caractères suffisent, y compris pour la Corse (« 20 » →
 * `FR20`, qui n'existe pas comme zone) — c'est le référentiel qui tranche, pas
 * cette fonction : elle propose, l'écran vérifie que la zone existe.
 */
export function zoneDe(
  codePostal: string | null,
  pays: string | null,
  connus: readonly Pays[],
): string | null {
  if (codePostal === null || pays === null) return null;
  const chiffres = codePostal.trim().replace(/\s/g, '');
  if (chiffres.length < 2) return null;
  const iso = codeIso(pays, connus);
  if (iso === null) return null;
  return `${iso}${chiffres.slice(0, 2).toUpperCase()}`;
}

/**
 * Le code ISO d'un pays écrit en toutes lettres.
 *
 * `sites.pays` est du TEXTE LIBRE, avec « France » pour défaut — il n'a jamais
 * porté de code. La correspondance vient de la table `pays`, LUE EN BASE : une
 * première rédaction du 06/09/2026 l'avait recopiée ici en dur, et l'API en
 * cherchait une autre de son côté. Deux listes qui divergent dirigeraient un
 * affrètement vers le mauvais pays sans que rien ne le signale.
 *
 * La normalisation est celle de `app.range` en base — minuscules, sans accent,
 * sans espaces autour — et elle doit le rester : c'est sous cette forme que la
 * table range ses noms.
 *
 * Un code ISO déjà écrit passe tel quel : « ES » reste « ES ». Tout le reste
 * rend `null`, et l'écran demande alors la zone à la main plutôt que d'en
 * inventer une.
 */
export function codeIso(pays: string, connus: readonly Pays[]): string | null {
  const propre = pays
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

  if (/^[a-z]{2}$/.test(propre)) return propre.toUpperCase();
  return connus.find((p) => p.nom === propre)?.iso ?? null;
}

/** La zone existe-t-elle dans le référentiel ? */
export function zoneConnue(code: string | null, zones: readonly Zone[]): boolean {
  if (code === null) return false;
  return zones.some((z) => z.code === code);
}

/**
 * Le nom d'une zone, ou son code si le référentiel ne la connaît pas.
 *
 * Rendre le code brut plutôt que rien : le jour où une zone est saisie à la
 * main sans exister, l'écran l'affiche telle quelle au lieu d'une case vide
 * dont personne ne comprendrait la cause. Même règle que `statutDit`.
 */
export function zoneDite(code: string | null, zones: readonly Zone[]): string | null {
  if (code === null || code === '') return null;
  return zones.find((z) => z.code === code)?.nom ?? code;
}

/** « FR45 → ES-NAT · T6 », ou ce qu'on en sait. */
export function axeDit(critere: {
  readonly depart: string | null;
  readonly arrivee: string | null;
  readonly vehicule: string | null;
}): string | null {
  const morceaux: string[] = [];
  if (critere.depart !== null && critere.depart !== '') {
    morceaux.push(critere.depart);
  }
  if (critere.arrivee !== null && critere.arrivee !== '') {
    morceaux.push(`→ ${critere.arrivee}`);
  }
  const axe = morceaux.join(' ');
  if (critere.vehicule !== null && critere.vehicule !== '') {
    return axe === '' ? critere.vehicule : `${axe} · ${critere.vehicule}`;
  }
  return axe === '' ? null : axe;
}

/**
 * « 4 transporteurs sur 337 », ou le décompte qui explique un écran vide.
 *
 * C'EST LA PHRASE QUI FAIT L'ÉCRAN. Sans elle, on lit « aucun résultat » et
 * l'on ne sait pas s'il faut élargir l'axe ou accepter un autre matériel.
 * La compétence le demande en toutes lettres : « si aucun transporteur ne
 * matche, le dire et proposer d'élargir ».
 */
export function decompteRecherche(
  trouves: number,
  explication: ExplicationVide | null,
): string {
  if (trouves > 0) {
    const tete =
      trouves > 1 ? `${String(trouves)} transporteurs` : `${String(trouves)} transporteur`;
    return explication === null
      ? tete
      : `${tete} sur ${String(explication.actifs)}`;
  }

  if (explication === null) return 'Aucun transporteur.';

  const morceaux: string[] = [];
  if (explication.sur_l_axe > 0) {
    morceaux.push(
      `${String(explication.sur_l_axe)} ${explication.sur_l_axe > 1 ? 'font' : 'fait'} cet axe, mais sans ce matériel`,
    );
  }
  if (explication.avec_le_materiel > 0) {
    morceaux.push(
      `${String(explication.avec_le_materiel)} ${explication.avec_le_materiel > 1 ? 'ont' : 'a'} ce matériel, mais ne ${explication.avec_le_materiel > 1 ? 'font' : 'fait'} pas cet axe`,
    );
  }

  if (morceaux.length === 0) {
    // Les deux filtres écartent tout : ce n'est plus un réglage à relâcher,
    // c'est une couverture qui manque dans l'annuaire.
    return `Aucun transporteur — aucun des ${String(explication.actifs)} actifs ne fait cet axe ni ne porte ce matériel.`;
  }
  return `Aucun transporteur — ${morceaux.join(' · ')}.`;
}

/**
 * Le lieu d'un transporteur, en une ligne : « 28630 Nogent-le-Phaye ».
 *
 * `null` quand rien n'est renseigné, plutôt qu'une chaîne vide qui laisserait
 * une case bizarrement blanche.
 */
export function lieuDit(t: {
  readonly code_postal: string | null;
  readonly ville: string | null;
}): string | null {
  const ligne = [t.code_postal, t.ville]
    .filter((m): m is string => m !== null && m.trim() !== '')
    .join(' ');
  return ligne === '' ? null : ligne;
}

// ---------------------------------------------------------------------------
// La marge
// ---------------------------------------------------------------------------

export interface Marge {
  /** En euros, prix de vente moins prix d'achat. */
  readonly montant: number;
  /** En pour-cent DU PRIX DE VENTE — c'est la maille du cadrage. */
  readonly taux: number;
  /** `vert` au-dessus de 10 %, `orange` en dessous, `rouge` à perte. */
  readonly couleur: 'vert' | 'orange' | 'rouge';
}

/**
 * La marge d'un affrètement, calculée de deux chiffres figés.
 *
 * ELLE N'EST STOCKÉE NULLE PART. La stocker serait un troisième nombre à tenir
 * d'accord avec les deux autres — même règle que `statutDeduit` et que le solde
 * d'emballage.
 *
 * **LA MARGE SE CALCULE SUR LE PRIX DE VENTE**, dit le cadrage §6 en toutes
 * lettres. 2 450 vendus, 1 940 achetés : 510 € de marge, soit 20,8 % — et non
 * 26,3 %, qui serait le taux rapporté à l'achat.
 *
 * `null` quand l'un des deux manque : un ordre sans prix de vente n'a pas de
 * marge, et écrire « 100 % » serait inventer.
 *
 * Les seuils viennent du cadrage : vert, orange sous 10 %, rouge à perte.
 */
export function marge(
  prixVente: string | null,
  prixAchat: string | null,
): Marge | null {
  if (prixVente === null || prixAchat === null) return null;
  const vente = Number(prixVente);
  const achat = Number(prixAchat);
  if (!Number.isFinite(vente) || !Number.isFinite(achat)) return null;
  // Une vente à zéro ne se rapporte à rien : la division n'aurait pas de sens.
  if (vente === 0) return null;

  const montant = vente - achat;
  const taux = (montant / vente) * 100;
  return {
    montant,
    taux,
    couleur: montant < 0 ? 'rouge' : taux < 10 ? 'orange' : 'vert',
  };
}

/** « +510,00 € · 20,8 % », ou « −120,00 € · −5,2 % ». */
export function margeDite(m: Marge): string {
  const euros = m.montant.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const taux = m.taux.toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const signe = m.montant > 0 ? '+' : '';
  return `${signe}${euros} € · ${taux} %`;
}

/** Ce que le panneau d'affrètement demande. */
export interface SaisieAffretement {
  readonly transporteurId: string;
  readonly prixAchat: string;
  readonly contactNom: string;
  readonly instructions: string;
}

/** Le panneau vierge — l'axe n'y figure pas : il se déduit, il ne se saisit pas. */
export const AFFRETEMENT_VIDE: SaisieAffretement = {
  transporteurId: '',
  prixAchat: '',
  contactNom: '',
  instructions: '',
};

/**
 * Ce que la route `POST /ordres-transport/:id/affretement` attend.
 *
 * NI L'AXE NI LE NOM DU TRANSPORTEUR : l'API les lit elle-même et les fige. Les
 * envoyer d'ici laisserait un écran poser un axe qui n'est pas celui des étapes.
 */
export function corpsAffretement(
  saisie: SaisieAffretement,
): Readonly<Record<string, string>> {
  const contact = saisie.contactNom.trim();
  const instructions = saisie.instructions.trim();
  return {
    transporteur_id: saisie.transporteurId,
    prix_achat: saisie.prixAchat.trim().replace(',', '.'),
    ...(contact === '' ? {} : { contact_nom: contact }),
    ...(instructions === '' ? {} : { instructions }),
  };
}

/**
 * LE DÉROULANT DU PANNEAU : ceux qui font l'axe, puis tous les autres.
 *
 * DÉCISION DU 07/09/2026, contre le cadrage du 05/09. Le panneau ne proposait
 * QUE les transporteurs dont la couverture correspond à l'axe. Deux constats
 * l'ont fait changer :
 *
 *   - la couverture n'est presque jamais saisie. Sur la base réelle de TNS, au
 *     jour de la bascule, AUCUN transporteur n'en avait — le filtre écartait
 *     donc tout le monde, et le panneau ne servait à rien ;
 *   - même renseignée, elle ne dit pas tout. Un affréteur appelle régulièrement
 *     quelqu'un hors de sa zone déclarée : un retour à vide, une relation, un
 *     dépannage. Lui interdire l'écran serait lui interdire son métier.
 *
 * Le classement du moteur n'est PAS perdu pour autant : ceux qui font l'axe
 * restent en tête, dans l'ordre qu'il a donné (nombre de transports, dernier
 * affrètement, nom). Les autres suivent, par nom.
 *
 * `tous` vient de l'annuaire et ne porte pas les compteurs : c'est voulu. Un
 * transporteur hors axe n'a pas de « pourquoi celui-là » à afficher.
 */
export interface GroupesTransporteurs {
  /** Ceux que le moteur a retenus, dans SON ordre. */
  readonly axe: readonly { readonly id: string; readonly texte: string }[];
  /** Tous les autres, actifs, par nom. */
  readonly autres: readonly { readonly id: string; readonly texte: string }[];
}

export function grouperTransporteurs(
  proposes: readonly {
    readonly id: string;
    readonly nom: string;
    readonly transports: number;
  }[],
  tous: readonly { readonly id: string; readonly nom: string; readonly actif: boolean }[],
): GroupesTransporteurs {
  const surLAxe = new Set(proposes.map((t) => t.id));

  const axe = proposes.map((t) => ({
    id: t.id,
    // Le compteur explique le rang. Zéro ne s'écrit pas : « 0 transports »
    // serait du bruit sur une base neuve, où ils le sont tous.
    texte: t.transports > 0 ? `${t.nom} — ${String(t.transports)} transports` : t.nom,
  }));

  const autres = tous
    .filter((t) => t.actif && !surLAxe.has(t.id))
    .slice()
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
    .map((t) => ({ id: t.id, texte: t.nom }));

  return { axe, autres };
}

/**
 * La valeur réservée de l'entrée « + Nouveau transporteur… ».
 *
 * Une valeur qu'aucun identifiant ne peut prendre : les identifiants sont des
 * UUID, et celui-ci ne l'est pas. Elle ne part jamais à l'API — l'écran ouvre
 * le formulaire de création et remet le choix à vide.
 */
export const CHOIX_NOUVEAU = '+nouveau';

// ---------------------------------------------------------------------------
// L'onglet « Affrétés » — ce qui est déjà confié
// ---------------------------------------------------------------------------

/**
 * UNE LIGNE DE L'ONGLET AFFRÉTÉS.
 *
 * AUCUNE ROUTE NOUVELLE. La liste des ordres rend déjà `affretement_id`,
 * `affrete_par` et `prix_achat` — ils sont dans `COLONNES_OT`
 * (`apps/api/src/ordres_transport.ts` ligne 71) et donc dans la réponse de
 * `GET /ordres-transport`. Écrire une route « les affrètements » aurait fait
 * une seconde source pour la même chose, et deux sources finissent par ne plus
 * dire pareil.
 *
 * La marge est CALCULÉE ici, à partir des deux prix. Elle n'est stockée nulle
 * part — c'est la règle du cadrage : ce qui se déduit ne se stocke pas.
 */
export interface LigneAffretee {
  readonly id: string;
  readonly numero: string;
  readonly client: string;
  /** « FR45 → ES28 », ou `null` quand un pays manque sur une étape. */
  readonly axe: string | null;
  readonly transporteur: string | null;
  readonly achat: string | null;
  readonly vente: string | null;
  readonly marge: Marge | null;
}

/**
 * Les ordres affrétés d'une liste, les plus récents d'abord.
 *
 * Le tri suit le numéro décroissant, comme la liste des ordres : c'est l'ordre
 * dans lequel ils ont été créés, et celui auquel l'exploitation est habituée.
 * L'API les rend déjà ainsi ; on ne s'en remet pas à elle pour autant — un
 * changement de tri côté API ne doit pas se voir ici sans qu'on le décide.
 */
export function lignesAffretees(
  ordres: readonly {
    readonly id: string;
    readonly numero: string;
    readonly client: string;
    readonly zone_depart: string | null;
    readonly zone_arrivee: string | null;
    readonly affretement_id: string | null;
    readonly affrete_par: string | null;
    readonly prix_achat: string | null;
    readonly prix_vente: string | null;
  }[],
): readonly LigneAffretee[] {
  return ordres
    .filter((o) => o.affretement_id !== null)
    .slice()
    .sort((a, b) => b.numero.localeCompare(a.numero, 'fr'))
    .map((o) => ({
      id: o.id,
      numero: o.numero,
      client: o.client,
      axe: axeDit({ depart: o.zone_depart, arrivee: o.zone_arrivee, vehicule: null }),
      transporteur: o.affrete_par,
      achat: o.prix_achat,
      vente: o.prix_vente,
      marge: marge(o.prix_vente, o.prix_achat),
    }));
}

/**
 * « 3 ordres affrétés », « 1 ordre affrété », « aucun ordre affrété ».
 *
 * Le décompte plutôt qu'un tableau vide muet : c'est la même règle que dans la
 * recherche — un écran vide qui n'explique pas est un écran raté.
 */
export function decompteAffretes(combien: number): string {
  if (combien === 0) return 'Aucun ordre affrété sur cette période.';
  return combien > 1 ? `${String(combien)} ordres affrétés` : '1 ordre affrété';
}

// ---------------------------------------------------------------------------
// LE SÉLECTEUR D'ORDRE — « pour quel transport cherche-t-on une solution ? »
// ---------------------------------------------------------------------------

/**
 * UN ORDRE PROPOSÉ AU SÉLECTEUR DE LA RECHERCHE.
 *
 * Demandé le 09/09/2026 : « dans le menu chercher un transporteur on doit
 * avoir un sélecteur d'OT pour choisir l'OT où l'on cherche une solution ».
 *
 * C'est le retour au cadrage §2, dont l'écran s'était écarté : « dans Stafa,
 * l'axe et le matériel NE SE SAISISSENT PAS — ils se déduisent de l'ordre ».
 * Les trois listes déroulantes restent, mais on n'a plus à les remplir à la
 * main quand on part d'un transport réel.
 *
 * IL PRÉ-REMPLIT, IL NE VERROUILLE PAS. Tout l'écran est construit pour qu'on
 * RELÂCHE un filtre quand personne ne sort — c'est la raison d'être du
 * décompte de `decompteRecherche`. Un axe verrouillé par l'ordre tuerait ce
 * mécanisme. Et chercher sans ordre reste utile : « qui fait Chartres →
 * Madrid ? » se demande avant d'avoir le transport.
 *
 * AUCUNE ROUTE NOUVELLE, pour la même raison que l'onglet « Affrétés » :
 * `GET /ordres-transport` rend déjà `zone_depart`, `zone_arrivee` et
 * `gabarit_code` — ils sont dans `COLONNES_OT`. Une route « les ordres à
 * affréter » aurait fait une seconde source pour la même liste.
 */
export interface OrdreACouvrir {
  readonly id: string;
  /** « OT-0412 · VPK · FR28 → FR45 · T6 » — ce que la liste déroulante montre. */
  readonly texte: string;
  /** Les trois filtres que ce choix pose. Vides quand l'ordre ne les donne pas. */
  readonly depart: string;
  readonly arrivee: string;
  readonly vehicule: string;
  /** Faux quand une étape n'a pas de pays : l'axe ne se déduit alors pas. */
  readonly axeConnu: boolean;
}

/**
 * Les ordres pour lesquels chercher un sous-traitant a un sens.
 *
 * DEUX EXCLUSIONS, ET PAS UNE DE PLUS :
 *
 *   - **les annulés** — l'API refuse déjà de les affréter (`ordre_annule`,
 *     `affretement.ts`), les proposer serait promettre un geste impossible ;
 *   - **ceux qui ont déjà un affrètement actif** — le cadrage §6 : « un seul
 *     affrètement actif par ordre ». Un ordre décommandé repasse dans la liste
 *     de lui-même, puisque l'annulation efface `affretement_id`.
 *
 * **ON N'EXCLUT PAS LES ORDRES DÉJÀ POSÉS SUR UN CAMION**, et c'est délibéré :
 * un camion qui casse le matin se sous-traite l'après-midi. Les écarter
 * fermerait l'écran sur le cas où l'on en a le plus besoin.
 *
 * **ON N'EXCLUT PAS NON PLUS LES LIVRÉS.** La saisie d'un affrètement arrive
 * souvent après le transport, et un ordre livré qu'on n'a pas encore rattaché
 * à son sous-traitant doit rester atteignable.
 *
 * Tri par numéro décroissant, comme la liste des ordres et l'onglet Affrétés :
 * les derniers créés en tête, c'est là qu'on travaille.
 */
export function ordresACouvrir(
  ordres: readonly {
    readonly id: string;
    readonly numero: string;
    readonly client: string;
    readonly statut: string;
    readonly gabarit_code: string | null;
    readonly zone_depart: string | null;
    readonly zone_arrivee: string | null;
    readonly affretement_id: string | null;
  }[],
): readonly OrdreACouvrir[] {
  return ordres
    .filter((o) => o.affretement_id === null && o.statut !== 'annule')
    .slice()
    .sort((a, b) => b.numero.localeCompare(a.numero, 'fr'))
    .map((o) => {
      const axeConnu = o.zone_depart !== null && o.zone_arrivee !== null;
      // L'AXE ÉCRIT EN CLAIR, ou dit manquant. Une case vide laisserait croire
      // que le transport part de partout ; « axe inconnu » dit qu'il faut
      // choisir les zones à la main — et pourquoi.
      const axe = axeConnu
        ? `${String(o.zone_depart)} → ${String(o.zone_arrivee)}`
        : 'axe inconnu';
      const morceaux = [o.numero, o.client, axe];
      if (o.gabarit_code !== null) morceaux.push(o.gabarit_code);
      return {
        id: o.id,
        texte: morceaux.join(' · '),
        // `??` et non `?? ''` sur une chaîne vide : l'API rend `null`, jamais
        // « ». Les filtres, eux, emploient la chaîne vide pour « Partout ».
        depart: o.zone_depart ?? '',
        arrivee: o.zone_arrivee ?? '',
        // LE MÊME RÉFÉRENTIEL DES DEUX CÔTÉS, et c'est ce qui rend le
        // pré-remplissage possible sans traduire : `ordres_transport.
        // gabarit_code` et `transporteur_vehicules.vehicule_code` pointent
        // tous deux sur `vehicules (code)` — vérifié dans les migrations
        // `20260904090000_ordres_transport.sql` et `20260903160000_
        // vehicules_et_zones.sql`.
        vehicule: o.gabarit_code ?? '',
        axeConnu,
      };
    });
}

/**
 * Ce que le sélecteur dit quand il n'a rien à proposer.
 *
 * Même règle que partout dans cet écran : un déroulant vide sans un mot laisse
 * croire à une panne. Ici, ce n'est presque jamais une panne — c'est que tout
 * est déjà affrété, ou que la fenêtre de dates ne couvre rien.
 */
export function decompteACouvrir(combien: number): string {
  if (combien === 0) return 'Aucun ordre à couvrir sur cette période.';
  return combien > 1
    ? `${String(combien)} ordres à couvrir`
    : '1 ordre à couvrir';
}
